/**
 * /reasoning-efficiency — first-to-market step-to-resolution dashboard.
 *
 * No competitor measures this yet (May 2026, per Nova). It scores how
 * well your AI's reasoning loop converts tokens into resolved intents.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Brain, Target, TrendingDown, Layers, AlertCircle, RefreshCw, ExternalLink } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import BrandTitle from '../components/BrandTitle';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Stat = ({ label, value, sub, color = 'text-white', testId }) => (
  <div className="rounded-xl border border-white/10 bg-slate-900/60 shadow-sm p-5" data-testid={testId}>
    <div className="text-[10px] font-bold tracking-widest text-slate-500 uppercase mb-1">{label}</div>
    <div className={`text-3xl font-extrabold tabular-nums ${color}`}>{value}</div>
    {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
  </div>
);

const BUCKET_BADGE = {
  healthy: { label: 'HEALTHY', cls: 'bg-emerald-100 text-emerald-300 border-emerald-300' },
  okay: { label: 'OKAY', cls: 'bg-amber-100 text-amber-300 border-amber-300' },
  poor: { label: 'POOR', cls: 'bg-rose-100 text-rose-300 border-rose-300' },
  no_data: { label: 'NO DATA', cls: 'bg-slate-900/60 text-slate-300 border-white/15' },
};

const ReasoningEfficiency = () => {
  const [summary, setSummary] = useState(null);
  const [leaderboard, setLeaderboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(7);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const headers = { 'Authorization': `Bearer ${localStorage.getItem('token') || ''}` };
      const [s, l] = await Promise.all([
        fetch(`${API}/reasoning-efficiency/summary?days=${days}`, { headers }).then(r => r.json()),
        fetch(`${API}/reasoning-efficiency/leaderboard?days=${days}&limit=10`, { headers }).then(r => r.json()),
      ]);
      setSummary(s);
      setLeaderboard(l);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); /* eslint-disable-next-line */ }, [days]);

  const bucket = BUCKET_BADGE[summary?.health_bucket] || BUCKET_BADGE.no_data;

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100" data-testid="reasoning-efficiency-page">
      <main className="max-w-6xl mx-auto px-6 py-10">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
          <div>
            <Badge className="bg-emerald-100 border-emerald-300 text-emerald-300 px-3 py-1 text-[11px] tracking-[0.15em] uppercase font-semibold mb-3" data-testid="rep-badge">
              <Brain className="w-3 h-3 mr-1.5 inline" /> Reasoning Efficiency · first-to-market
            </Badge>
            <h1 className="text-3xl sm:text-4xl font-bold tracking-tight"><BrandTitle>Reasoning Efficiency Engine</BrandTitle></h1>
            <p className="text-slate-300 mt-1 max-w-2xl">
              Token counters tell you what you spent. CARTIE tells you whether the tokens <em>resolved an intent</em>.
              No other FinOps tool measures this — yet.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <select
              value={days}
              onChange={(e) => setDays(parseInt(e.target.value, 10))}
              className="rounded-lg border border-white/15 px-3 py-1.5 text-sm bg-slate-900/60"
              data-testid="rep-window-select"
            >
              <option value={7}>Last 7 days</option>
              <option value={14}>Last 14 days</option>
              <option value={30}>Last 30 days</option>
            </select>
            <button onClick={fetchAll} className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-slate-950 px-3 py-1.5 text-sm font-semibold" data-testid="rep-refresh">
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </button>
          </div>
        </div>

        {/* Headline */}
        <div className="rounded-2xl bg-emerald-500/10 border border-emerald-500/30 p-6 mb-8" data-testid="rep-headline">
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div>
              <div className="text-[10px] font-bold tracking-widest text-emerald-300 uppercase mb-2">Reasoning health</div>
              <Badge className={`${bucket.cls} px-3 py-1 text-[11px] tracking-[0.15em] uppercase font-bold`} data-testid="rep-bucket">{bucket.label}</Badge>
              <p className="text-slate-200 mt-3 max-w-xl text-sm">
                {summary?.health_bucket === 'no_data'
                  ? 'Tag at least 20 traces with thumbs-up/down to surface a health score.'
                  : `Of the $${summary?.total_cost_usd?.toLocaleString() || 0} spent on agent traces, ${summary?.loop_waste_pct || 0}% leaked on unresolved loops.`}
              </p>
            </div>
            <Link to="/traces" className="inline-flex items-center gap-1.5 rounded-full bg-slate-900 text-emerald-300 px-4 py-2 text-sm font-semibold hover:bg-slate-800 transition-colors">
              Open trace viewer <ExternalLink className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
          <Stat label="Cost / Resolution" value={summary?.cost_per_resolution_usd != null ? `$${summary.cost_per_resolution_usd.toFixed(4)}` : '—'} sub="Total cost ÷ resolved intents" testId="rep-stat-cpr" />
          <Stat label="Avg Steps / Resolve" value={summary?.avg_steps_to_resolution != null ? summary.avg_steps_to_resolution : '—'} sub="Lower is better" testId="rep-stat-steps" />
          <Stat label="Loop Waste %" value={summary?.loop_waste_pct != null ? `${summary.loop_waste_pct}%` : '—'} color="text-rose-600" sub="$ on unresolved traces" testId="rep-stat-loopwaste" />
          <Stat label="Feedback Coverage" value={summary?.feedback_coverage_pct != null ? `${summary.feedback_coverage_pct}%` : '—'} sub="Traces with thumbs-up/down" testId="rep-stat-coverage" />
        </div>

        {/* Leaderboard */}
        <div className="rounded-2xl bg-slate-900/60 border border-white/10 shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-white/10">
            <h2 className="text-base font-bold text-white flex items-center gap-2">
              <Target className="w-4 h-4 text-emerald-500" />
              Worst features by cost-per-resolved-intent
            </h2>
            <p className="text-xs text-slate-500 mt-1">The features wasting most $ per useful answer.</p>
          </div>
          {(!leaderboard?.rows || leaderboard.rows.length === 0) ? (
            <div className="px-6 py-10 text-center text-slate-500">
              <AlertCircle className="w-8 h-8 mx-auto text-slate-400 mb-2" />
              No data yet. Tag traces with thumbs-up/down to populate.
            </div>
          ) : (
            <table className="w-full text-sm" data-testid="rep-leaderboard">
              <thead className="bg-slate-900/60 border-b border-white/10">
                <tr>
                  <th className="text-left px-6 py-3 font-semibold text-slate-300">Feature</th>
                  <th className="text-right px-3 py-3 font-semibold text-slate-300">Traces</th>
                  <th className="text-right px-3 py-3 font-semibold text-slate-300">Total $</th>
                  <th className="text-right px-3 py-3 font-semibold text-slate-300">$ / Resolution</th>
                  <th className="text-right px-3 py-3 font-semibold text-slate-300">Avg Steps</th>
                  <th className="text-right px-6 py-3 font-semibold text-slate-300">Loop %</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10">
                {leaderboard.rows.map((r, i) => {
                  const loopPct = r.traces > 0 ? Math.round((r.unresolved / r.traces) * 100) : 0;
                  return (
                    <tr key={r.feature_slug} className="hover:bg-slate-900/60" data-testid={`rep-lb-row-${i}`}>
                      <td className="px-6 py-3 font-mono text-emerald-300">{r.feature_slug}</td>
                      <td className="px-3 py-3 text-right text-slate-200 tabular-nums">{r.traces}</td>
                      <td className="px-3 py-3 text-right text-white tabular-nums">${r.cost_usd.toLocaleString()}</td>
                      <td className="px-3 py-3 text-right text-white tabular-nums font-semibold">{r.cost_per_resolution_usd != null ? `$${r.cost_per_resolution_usd.toFixed(4)}` : '—'}</td>
                      <td className="px-3 py-3 text-right text-slate-200 tabular-nums">{r.avg_steps}</td>
                      <td className="px-6 py-3 text-right">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${loopPct >= 25 ? 'bg-rose-100 text-rose-300' : loopPct >= 10 ? 'bg-amber-100 text-amber-300' : 'bg-emerald-100 text-emerald-300'}`}>
                          {loopPct}%
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Educational footer */}
        <Card className="bg-slate-900/60 border-white/10 mt-8">
          <CardContent className="p-5">
            <h3 className="font-bold text-white flex items-center gap-2 mb-2"><Layers className="w-4 h-4 text-emerald-500" /> How this metric works</h3>
            <p className="text-sm text-slate-200 leading-relaxed">
              For every trace, we record total cost and step count from your existing trace pipeline. A trace is <strong>resolved</strong> when an
              engineer (or end-user) gives a thumbs-up via the feedback chip in the Trace Viewer. <strong>Cost per Resolution</strong> = Σ(resolved trace cost) ÷ count(resolved).
              <strong>Loop waste %</strong> = Σ(unresolved trace cost) ÷ total cost.
              A 20% loop waste rate on a $50k/mo agentic workflow is $10k/mo of unproductive tokens — that's the leak this engine surfaces.
            </p>
            <p className="text-xs text-slate-500 mt-2 italic">
              First-to-market: as of May 2026 no other FinOps tool measures step-to-resolution efficiency.
            </p>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default ReasoningEfficiency;
